/* import __COLOCATED_TEMPLATE__ from './custom-csat-composer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { BlocksDocument } from '@intercom/composer-ember';
import ComposerConfig from 'embercom/objects/workflows/graph-editor/chat-message/composer-config';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    content?: string; // Plain text content (for backward compatibility)
    placeholder?: string;
    onChange?: (content: string) => void; // Called with plain text content
    onFocus?: () => void;
  };
}

export default class CustomCsatComposer extends Component<Signature> {
  @service declare intl: $TSFixMe;
  @service declare appService: AppService;
  @service declare attributeService: $TSFixMe;

  @tracked blocksDoc: BlocksDocument;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    // Initialize with content or empty
    // If content is provided as plain text, convert to blocks
    let initialBlocks = this.args.content ? [{ type: 'paragraph', text: this.args.content }] : [];
    this.blocksDoc = new BlocksDocument(initialBlocks);
  }

  get app() {
    return this.appService.app;
  }

  get composerConfig() {
    return new ComposerConfig({
      app: this.app,
      resolver: new AttributeInfoResolver({
        attributes: [
          ...this.attributeService.userEditorMetadataAndConversationAttributes,
          ...this.attributeService.conversationAttributesForCustomAction,
        ],
        customObjectAttributes: this.attributeService.customObjectsAttributeDescriptors,
        includeAppAttributes: true,
        includeCustomObjectAttributes: true,
        includeCustomActionAttributes: true,
        localAttributes: [], // Empty array for CSAT composer
        allLocalAttributes: [], // Empty array for CSAT composer
      }),
      placeholder: this.args.placeholder || this.intl.t('custom-csat-composer.placeholder'),
      allowMentions: false, // Disable mentions for CSAT messages
      hideTextFormatter: false, // Show text formatting tools including links
    });
  }

  @action
  updateBlocks(blocksDoc: BlocksDocument) {
    // Store the full blocks document to preserve rich content (links, attributes, formatting)
    this.blocksDoc = blocksDoc;

    // Convert to plain text for the onChange callback (for backward compatibility)
    let content = blocksDoc.blocks
      .map((block: any) => block.text)
      .join('\n')
      .trim();
    this.args.onChange?.(content);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::AskForRating::CustomCsatComposer': typeof CustomCsatComposer;
    'workflows/graph-editor/node-items/steps/fin/sections/ask-for-rating/custom-csat-composer': typeof CustomCsatComposer;
  }
}
